if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        try {
          this.section = {
            settings: JSON.parse(this.getAttribute('data-section-settings') || '{}'),
          };
        } catch (error) {
          this.section = { settings: {} };
        }
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.mobile-dot-navigation'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        this.prependMedia = this.dataset.disablePrepend != 'true';
        if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
        this.filteringOption = this.dataset.filteringOption;
        this.productSpecificFiltering = this.dataset.productSpecificFiltering === 'true';
        this.filterOptionValues = [];
        try {
          const raw = this.dataset.filterOptionValues;
          if (raw) this.filterOptionValues = JSON.parse(raw);
        } catch (e) {
          this.filterOptionValues = [];
        }
        this.videoIntersectionObserver = null;
        this.videoObserverTarget = null;
        this.videoObservers = [];

        const isGridLayout = ['columns', 'columns_1', 'stacked'].includes(this.dataset.desktopLayout || '');

        if (this.elements.viewer) {
          if (isGridLayout) {
            this.initVideoAutoplayForGridLayout();
          } else {
            this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
            const initialActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
            if (initialActiveMedia) this.playActiveMedia(initialActiveMedia);
          }
        }

        // Add thumbnail event listeners
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const thumbnailButton = mediaToSwitch.querySelector('button');
            if (!thumbnailButton) return;

            thumbnailButton.addEventListener('click', () => {
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
            });
          });
          this.initThumbnailRowAlignment();
          this.initThumbnailCarouselScrollEffects();
        }
        
        // Add dot navigation event listeners
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', () => {
              this.setActiveMedia(dot.dataset.target, false);
            });
          });
        }
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Listen for variant changes if filtering is enabled
        if (this.filteringOption) {
          document.addEventListener('variant:change', this.handleVariantChange.bind(this));
          // Initial stacked layout fix: ensure first visible gets full width when filtering may hide first child
          if (this.dataset.desktopLayout === 'stacked' && this.elements.viewer) {
            this.updateStackedLayoutFirstVisible();
          }
        }

        // Play overlay click (when autoplay disabled)
        this.addEventListener('click', this.handleVideoPlayOverlayClick.bind(this));
      }

      handleVideoPlayOverlayClick(event) {
        const overlay = event.target.closest('.product-media-video-play-overlay');
        if (!overlay) return;
        const wrapper = overlay.closest('.product-media-video-wrapper');
        const video = wrapper?.querySelector('video');
        if (video) {
          if (!overlay.dataset.listenersAdded) {
            video.addEventListener('pause', () => overlay.classList.remove('hidden'));
            video.addEventListener('ended', () => overlay.classList.remove('hidden'));
            overlay.dataset.listenersAdded = 'true';
          }
          video.play().catch(() => {});
          overlay.classList.add('hidden');
        }
      }

      onSlideChanged(event) {
        // Update active media and play when slide changes (e.g. via slider scroll)
        const currentElement = event.detail?.currentElement;
        if (currentElement) {
          this.elements.viewer.querySelectorAll('.product__media-item').forEach((el) => el.classList.remove('is-active'));
          currentElement.classList.add('is-active');
          this.playActiveMedia(currentElement);
        }

        // Update thumbnail navigation
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          if (thumbnail && !thumbnail.classList.contains('hidden')) {
            this.setActiveThumbnail(thumbnail);
          }
        }
        
        // Update dot navigation
        if (this.elements.dots) {
          this.setActiveDot(event.detail.currentElement.dataset.mediaId);
        }
      }

      setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
        if (filtering && currentVariant) {
          // Use the dedicated filtering method
          this.updateMediaFiltering(currentVariant);
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && this.prependMedia) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          if (!this.section?.settings?.no_scroll) {
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        const isGridLayout = ['columns', 'columns_1', 'stacked'].includes(this.dataset.desktopLayout || '');
        if (!isGridLayout) this.playActiveMedia(activeMedia);

        if (filtering && currentVariant) {
          if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
          if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();

          // Update thumbnail navigation after filtering
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && !activeThumbnail.classList.contains('hidden')) {
              this.setActiveThumbnail(activeThumbnail);
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }
          
          // Update dot navigation after filtering
          if (this.elements.dots) {
            this.setActiveDot(mediaId);
          }
          
          return; // Skip the regular navigation update when filtering
        }

        if (!this.elements.thumbnails && !this.elements.dots) return;
        
        // Update thumbnail navigation (regular flow)
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
        
        // Update dot navigation (regular flow)
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      isVerticalThumbnailLayout() {
        const layout = this.dataset.desktopLayout || '';
        return (layout === 'vertical_left' || layout === 'vertical_right') && this.mql.matches;
      }

      getVisibleThumbnailItems() {
        const slider = this.elements.thumbnails?.slider;
        if (!slider) return [];
        return Array.from(slider.querySelectorAll('[id^="Slide-"]')).filter(
          (element) => !element.classList.contains('hidden') && element.clientHeight > 0
        );
      }

      getThumbnailItemOffset(items) {
        if (items.length > 1) {
          return items[1].offsetTop - items[0].offsetTop;
        }
        if (items.length === 1) {
          const sliderStyles = getComputedStyle(this.elements.thumbnails.slider);
          const gap =
            parseFloat(sliderStyles.rowGap) || parseFloat(sliderStyles.gap) || parseFloat(sliderStyles.gridGap) || 16;
          return items[0].offsetHeight + gap;
        }
        return 0;
      }

      isThumbnailVerticallyVisible(thumbnail, offset = 10) {
        const slider = this.elements.thumbnails?.slider;
        if (!slider || !thumbnail) return true;

        const sliderRect = slider.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();

        return (
          thumbRect.top >= sliderRect.top + offset && thumbRect.bottom <= sliderRect.bottom - offset
        );
      }

      scrollVerticalThumbnails(thumbnail, { revealNext = false } = {}) {
        const slider = this.elements.thumbnails?.slider;
        if (!slider || !thumbnail) return;

        const items = this.getVisibleThumbnailItems();
        const index = items.indexOf(thumbnail);
        if (index === -1) return;

        const itemOffset = this.getThumbnailItemOffset(items);
        if (!itemOffset) return;

        const edgeOffset = 10;
        const maxScroll = slider.scrollHeight - slider.clientHeight;
        const sliderRect = slider.getBoundingClientRect();
        const thumbRect = thumbnail.getBoundingClientRect();

        const isAboveView = thumbRect.top < sliderRect.top + edgeOffset;
        const isBelowView = thumbRect.bottom > sliderRect.bottom - edgeOffset;
        const isFullyVisible = !isAboveView && !isBelowView;

        if (isFullyVisible && !revealNext) return;

        let scrollTop = slider.scrollTop;

        if (isAboveView) {
          scrollTop = Math.max(0, slider.scrollTop - itemOffset);
        } else if (isBelowView) {
          scrollTop = Math.min(maxScroll, slider.scrollTop + itemOffset);
        }

        if (revealNext && index < items.length - 1) {
          const nextThumbnail = items[index + 1];
          const nextRect = nextThumbnail.getBoundingClientRect();
          const nextNotFullyVisible = nextRect.bottom > sliderRect.bottom - edgeOffset;

          if (nextNotFullyVisible) {
            scrollTop = Math.min(maxScroll, (isFullyVisible ? slider.scrollTop : scrollTop) + itemOffset);
          }
        }

        if (Math.abs(scrollTop - slider.scrollTop) > 1) {
          slider.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
      }

      shouldCenterThumbnailsWhenFit() {
        return this.section?.settings?.center_thumbnails_when_fit !== false;
      }

      getThumbnailListSlider() {
        if (!this.elements.thumbnails || this.isVerticalThumbnailLayout()) return null;
        const slider = this.elements.thumbnails.querySelector('.thumbnail-list.slider[id^="Slider-"]');
        return slider || null;
      }

      updateThumbnailRowAlignment() {
        if (!this.shouldCenterThumbnailsWhenFit()) {
          const slider = this.getThumbnailListSlider();
          if (slider) slider.classList.remove('thumbnail-list--centered');
          return;
        }

        const slider = this.getThumbnailListSlider();
        if (!slider) return;

        const tolerance = 2;
        const fits = slider.scrollWidth <= slider.clientWidth + tolerance;

        if (fits) {
          slider.classList.add('thumbnail-list--centered');
          slider.scrollLeft = 0;
        } else {
          slider.classList.remove('thumbnail-list--centered');
        }
      }

      shouldUpdateThumbnailCarouselScrollFade() {
        const layout = this.dataset.desktopLayout || '';
        const mobileThumbnails = this.section?.settings?.mobile_thumbnails;
        if (this.mql.matches) {
          return layout === 'thumbnail_slider';
        }
        return mobileThumbnails === 'show';
      }

      updateThumbnailCarouselScrollFade() {
        const thumbSlider = this.elements.thumbnails;
        if (!thumbSlider) return;

        if (!this.shouldUpdateThumbnailCarouselScrollFade()) {
          thumbSlider.classList.remove('is-fade-left', 'is-fade-right', 'is-arrow-left', 'is-arrow-right');
          return;
        }

        const list = this.getThumbnailListSlider();
        if (!list) return;

        const threshold = 2;
        const canScroll = list.scrollWidth > list.clientWidth + threshold;
        const isDesktop = this.mql.matches;
        const fadeEnabled = isDesktop
          ? thumbSlider.dataset.showDesktopThumbnailFade === 'true'
          : thumbSlider.dataset.showMobileThumbnailFade === 'true';
        const arrowsEnabled = isDesktop
          ? thumbSlider.dataset.showDesktopThumbnailArrows === 'true'
          : thumbSlider.dataset.showMobileThumbnailArrows === 'true';
        const prevBtn = thumbSlider.querySelector('.slider-button--prev');
        const nextBtn = thumbSlider.querySelector('.slider-button--next');

        if (!canScroll) {
          thumbSlider.classList.remove('is-fade-left', 'is-fade-right', 'is-arrow-left', 'is-arrow-right');
          if (prevBtn) prevBtn.setAttribute('disabled', 'disabled');
          if (nextBtn) nextBtn.setAttribute('disabled', 'disabled');
          return;
        }

        const atLeft = list.scrollLeft <= threshold;
        const atRight = list.scrollLeft + list.clientWidth >= list.scrollWidth - threshold;

        if (fadeEnabled) {
          thumbSlider.classList.toggle('is-fade-left', !atLeft);
          thumbSlider.classList.toggle('is-fade-right', !atRight);
        } else {
          thumbSlider.classList.remove('is-fade-left', 'is-fade-right');
        }

        if (arrowsEnabled) {
          thumbSlider.classList.toggle('is-arrow-left', !atLeft);
          thumbSlider.classList.toggle('is-arrow-right', !atRight);

          if (prevBtn) {
            if (atLeft) prevBtn.setAttribute('disabled', 'disabled');
            else prevBtn.removeAttribute('disabled');
          }
          if (nextBtn) {
            if (atRight) nextBtn.setAttribute('disabled', 'disabled');
            else nextBtn.removeAttribute('disabled');
          }
        } else {
          thumbSlider.classList.remove('is-arrow-left', 'is-arrow-right');
        }
      }

      initThumbnailCarouselScrollEffects() {
        const layout = this.dataset.desktopLayout || '';
        const mobileThumbnails = this.section?.settings?.mobile_thumbnails;
        if (layout !== 'thumbnail_slider' && mobileThumbnails !== 'show') return;

        const thumbSlider = this.elements.thumbnails;
        const list = this.getThumbnailListSlider();
        if (!thumbSlider || !list) return;

        const onScroll = () => this.updateThumbnailCarouselScrollFade();
        if (!list.dataset.thumbnailCarouselScrollBound) {
          list.dataset.thumbnailCarouselScrollBound = 'true';
          list.addEventListener('scroll', onScroll, { passive: true });
        }

        if (!this.thumbnailCarouselResizeObserver) {
          this.thumbnailCarouselResizeObserver = new ResizeObserver(onScroll);
          this.thumbnailCarouselResizeObserver.observe(list);
        }

        if (!this.thumbnailCarouselScrollResizeHandler) {
          this.thumbnailCarouselScrollResizeHandler = onScroll;
          window.addEventListener('resize', this.thumbnailCarouselScrollResizeHandler);
        }

        if (!this.thumbnailCarouselMqlHandler) {
          this.thumbnailCarouselMqlHandler = onScroll;
          this.mql.addEventListener('change', this.thumbnailCarouselMqlHandler);
        }

        requestAnimationFrame(onScroll);
      }

      initThumbnailRowAlignment() {
        if (!this.shouldCenterThumbnailsWhenFit()) return;

        const slider = this.getThumbnailListSlider();
        if (!slider) return;

        const runUpdate = () => this.updateThumbnailRowAlignment();
        requestAnimationFrame(runUpdate);

        if (!this.thumbnailRowResizeObserver) {
          this.thumbnailRowResizeObserver = new ResizeObserver(runUpdate);
          this.thumbnailRowResizeObserver.observe(slider);
        }

        if (!this.thumbnailRowAlignmentResizeHandler) {
          this.thumbnailRowAlignmentResizeHandler = () => runUpdate();
          window.addEventListener('resize', this.thumbnailRowAlignmentResizeHandler);
        }
      }

      scrollHorizontalThumbnailIntoView(thumbnail) {
        const slider = this.getThumbnailListSlider();
        if (!slider || !thumbnail) return;
        if (slider.classList.contains('thumbnail-list--centered')) return;

        const edgeOffset = 8;
        const thumbRect = thumbnail.getBoundingClientRect();
        const sliderRect = slider.getBoundingClientRect();

        const isFullyVisible =
          thumbRect.left >= sliderRect.left + edgeOffset &&
          thumbRect.right <= sliderRect.right - edgeOffset;

        if (isFullyVisible) return;

        const maxScroll = Math.max(0, slider.scrollWidth - slider.clientWidth);
        const scrollLeft = Math.min(
          maxScroll,
          Math.max(0, thumbnail.offsetLeft - (slider.clientWidth - thumbnail.offsetWidth) / 2)
        );

        slider.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.isVerticalThumbnailLayout()) {
          if (!this.isThumbnailVerticallyVisible(thumbnail)) {
            this.scrollVerticalThumbnails(thumbnail);
          }
          return;
        }

        this.scrollHorizontalThumbnailIntoView(thumbnail);
        this.updateThumbnailRowAlignment();
        this.updateThumbnailCarouselScrollFade();
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;

        this.elements.dots
          .querySelectorAll('.dot-navigation-dot')
          .forEach((dot) => {
            dot.classList.remove('active');
            dot.removeAttribute('aria-current');
          });
        
        const activeDot = this.elements.dots.querySelector(`[data-target="${mediaId}"]`);
        if (activeDot) {
          activeDot.classList.add('active');
          activeDot.setAttribute('aria-current', true);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      initVideoAutoplayForGridLayout() {
        const enableAutoplay = this.section?.settings?.enable_video_autoplay !== false;
        if (!enableAutoplay || !this.elements.viewer) return;

        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach((item) => {
          const deferredMedia = item.querySelector('.deferred-media');
          const nativeVideo = item.querySelector('video');
          if (!deferredMedia && !nativeVideo) return;

          const playMedia = () => {
            if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
              deferredMedia.loadContent(false);
            } else if (deferredMedia) {
              const video = deferredMedia.querySelector('video');
              const iframe = deferredMedia.querySelector('iframe');
              if (video) video.play().catch(() => {});
              if (iframe?.contentWindow) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"play"}', '*');
                }
              }
            }
            if (nativeVideo) nativeVideo.play().catch(() => {});
          };

          const pauseMedia = () => {
            if (deferredMedia) {
              const video = deferredMedia.querySelector('video');
              const iframe = deferredMedia.querySelector('iframe');
              if (video) video.pause();
              if (iframe?.contentWindow) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"pause"}', '*');
                }
              }
            }
            if (nativeVideo) nativeVideo.pause();
          };

          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                playMedia();
              } else {
                pauseMedia();
              }
            });
          }, { threshold: 0.25, rootMargin: '0px' });

          observer.observe(item);
          this.videoObservers.push({ observer, target: item });
        });
      }

      pauseAllGalleryMedia() {
        const gallery = this;
        gallery.querySelectorAll('video').forEach((v) => v.pause());
        gallery.querySelectorAll('.js-youtube').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        gallery.querySelectorAll('.js-vimeo').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
      }

      playActiveMedia(activeItem) {
        // Pause all media in this gallery first (including previous slide)
        this.pauseAllGalleryMedia();

        // Disconnect previous observer and explicitly pause its media
        if (this.videoIntersectionObserver && this.videoObserverTarget) {
          this.videoIntersectionObserver.unobserve(this.videoObserverTarget);
          this.videoObserverTarget = null;
        }

        const enableAutoplay = this.section?.settings?.enable_video_autoplay !== false;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        const nativeVideo = activeItem.querySelector('video');

        if (!enableAutoplay) {
          if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
            deferredMedia.loadContent(false);
          }
          return;
        }

        const playMedia = () => {
          if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
            deferredMedia.loadContent(false);
          } else if (deferredMedia) {
            const video = deferredMedia.querySelector('video');
            const iframe = deferredMedia.querySelector('iframe');
            if (video) video.play().catch(() => {});
            if (iframe?.contentWindow) {
              if (iframe.classList.contains('js-youtube')) {
                iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
              } else if (iframe.classList.contains('js-vimeo')) {
                iframe.contentWindow.postMessage('{"method":"play"}', '*');
              }
            }
          }
          if (nativeVideo) {
            nativeVideo.play().catch(() => {});
          }
        };

        const pauseMedia = () => {
          if (deferredMedia) {
            const video = deferredMedia.querySelector('video');
            if (video) video.pause();
          }
          if (nativeVideo) nativeVideo.pause();
        };

        const observerCallback = (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              playMedia();
            } else {
              pauseMedia();
            }
          });
        };

        this.playMediaGeneration = (this.playMediaGeneration || 0) + 1;
        const currentGeneration = this.playMediaGeneration;

        const observerCallbackWithGuard = (entries) => {
          if (currentGeneration !== this.playMediaGeneration) return;
          observerCallback(entries);
        };

        this.videoIntersectionObserver = new IntersectionObserver(observerCallbackWithGuard, {
          threshold: 0.25,
          rootMargin: '0px'
        });
        this.videoObserverTarget = activeItem;
        this.videoIntersectionObserver.observe(activeItem);

        // Play immediately if already in view
        const rect = activeItem.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInView) playMedia();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      handleVariantChange(event) {
        if (!this.filteringOption || !event.detail.variant) return;

        const currentVariant = event.detail.variant;

        this.updateMediaFiltering(currentVariant);

        const firstVisibleMedia = this.querySelector('.product__media-item:not(.hidden)');
        if (firstVisibleMedia) {
          const mediaId = firstVisibleMedia.dataset.mediaId;
          this.setActiveMedia(mediaId, false, true, currentVariant);
        }
      }

      updateStackedLayoutFirstVisible() {
        const isStackedLayout = this.dataset.desktopLayout === 'stacked';
        if (!isStackedLayout || !this.elements.viewer) return;
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach(item => item.classList.remove('product__media-item--full'));
        const firstVisible = this.elements.viewer.querySelector('.product__media-item:not(.hidden)');
        if (firstVisible) {
          firstVisible.classList.add('product__media-item--full');
        }
      }

      updateMediaFiltering(currentVariant) {
        if (!this.filteringOption || !currentVariant) return;

        // Filter all media items, thumbnails, and dots
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .dot-navigation-dot');
        allSlides.forEach(slide => {
          const slideAlt = slide.dataset.alt;
          const variantValue = currentVariant[this.filteringOption];
          // Show: no alt, always_display, matches selection, or alt text that is not a variant option value (shared/descriptive images)
          const shouldShow =
            slideAlt === 'always_display' ||
            !slideAlt ||
            slideAlt === '' ||
            slideAlt === variantValue ||
            !this.filterOptionValues.includes(slideAlt);

          if (shouldShow) {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });
        
        // Stacked layout: ensure first visible media item gets full width (fixes layout when first child is hidden)
        this.updateStackedLayoutFirstVisible();

        this.updateThumbnailRowAlignment();
        this.updateThumbnailCarouselScrollFade();
        
        // Also filter slider counter links if they exist
        const sliderLinks = this.querySelectorAll('.slider-counter__link');
        sliderLinks.forEach(link => {
          const linkAlt = link.dataset.alt;
          const variantValue = currentVariant[this.filteringOption];
          const shouldShow =
            linkAlt === 'always_display' ||
            !linkAlt ||
            linkAlt === '' ||
            linkAlt === variantValue ||
            !this.filterOptionValues.includes(linkAlt);

          if (shouldShow) {
            link.classList.remove('hidden');
          } else {
            link.classList.add('hidden');
          }
        });
      }
    }
  );
}
